import { useApolloClient, useMutation, useQuery } from "@apollo/react-hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CONSULTORY_PARAMETERS,
  CONSULTORY_PARAMETERS_AND_ORDER,
  CREATE_APPOINTMENT_HISTORY,
  GET_ALLERGY_PER_PATIENT,
  GET_APPOINTMENT_DATA,
  GET_AVAILABLE_QUANTITY,
  GET_CONSULTORY_SERVICES,
  GET_DIAGNOSES,
  GET_LAST_APPO_HISTO,
  GET_LICENSES_TYPES,
  GET_MEDICAL_STAFFS,
  GET_MEDICINE,
  GET_PRODUCT,
  GET_RECIPE_TYPE,
  GET_SPECIALITIES,
  OPTIONS,
  SAVE_ALLERGY_PER_PATIENT,
  UPDATE_PATIENT_DATE_OF_BIRTH,
  UPDATE_PATIENT_DENIES_HISTORY,
  VALIDATE_PATIENT_ALLERGY_GROUP,
} from "./queries";
import { isEmpty, uniqBy } from "lodash";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useToasts } from "react-toast-notifications";
import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import getDataAppointmentHistoryReport from "../../../utils/getDataReports.js/getDataAppointmentHistoryReport";
import handleShowReportAppointmentHistory from "../../../utils/getDataReports.js/handleShowReportAppointmentHistory";
import { fetchCompanyInfo } from "@dhn/shared/company-info";
import usePrescriptionAvailability, {
  NO_PRESCRIPTION_AVAILABLE_MESSAGE,
} from "../../../hooks/usePrescriptionAvailability";
import {
  getParameterGroupKey,
  getVisibleMedicalParameters,
  getVisibleParameterGroups,
  hasMedicalParameterValue,
  isMedicalParameterRequired,
  isSameMedicalParameterRow,
} from "./tabs/EvaluationParameters/utils";

const WEIGHT_PARAMETER_IDS = new Set([19, 20]);
const HEIGHT_PARAMETER_IDS = new Set([17, 18]);

const INITIAL_STATE = {
  data: [],
  dataAppointment: {},
  loading: false,
  loadingButton2: false,
  activeTab: 1,
  activeTabPrescriptions: 1,
  activeTabQuote: 1,
  activeTabParameterEvaluation: null,
  isLoadingTabs: false,
  isLoading: false,
  edited: false,
  removeServiceWithIdRef: false,
  isOpenModalConfirmEdit: false,
  isOpenModalDateOfBirth: false,
  isClinicalHistoryModalOpen: false,
  isLoadingModalDateOfBirth: false,
  isLoadingModal: false,
  tabGenerals: {
    reason: "",
    diseaseHistory: "",
  },
  tabDataParametersEvaluation: {
    dataSerched: [],
    dataParameterGroupOrder: [],
  },
  tabServices: {
    isOpenModal: false,
    dataNecesaryModal: { serviceId: null, result: "" },
    optionsDataServices: [],
    appointmentHistoryServices: [],
  },
  tabPrescription: {
    subTabMedicaments: {
      recipeTypes: [],
      selectedRecipeTipe: {},
      selectedMedicament: {},
      quantity: 1,
      frecuency: 1,
      frecuencyDaysHours: "",
      duration: 1,
      durationDaysWeeksMonths: "",
      doctorNote: "",
      appointmentHistoryMedicalPrecription: {
        precriptionDetails: [],
      },
    },
    subTabExamsLaboratory: {
      optionsLaboratoryServices: [],
      appointmentHistoryLaboratory: [],
    },
    subTabImageStudies: {
      optionsImageStudiesServices: [],
      appointmentHistoryImages: [],
    },
    subTabMedicalLicenses: {
      optionsMedicalLicenses: [],
      appointmentHistoryLicenses: {
        licensesTypes: {},
        days: 0,
        description: "",
      },
    },
    subTabReferences: {
      appointmentHistoryReferral: {},
    },
  },
  tabDiagnoses: {
    appointmentHistoryDiagnoses: [],
  },
  tabQuotes: {
    quotations: {},
  },
  tabAllergies: {
    data: [],
  },
};

const hasMedicalResultUploadFiles = (payload) =>
  [
    ...(payload?.appointmentHistoryLaboratory || []),
    ...(payload?.appointmentHistoryImages || []),
  ].some(
    (item) => Array.isArray(item?.files) && item.files.length > 0,
  );

const buildMedicalResultsPayload = ({ records, examIdField, examField, appointment }) =>
  (records || []).map((item) => {
    const itemFiles = Array.isArray(item?.files) ? item.files : [];
    const uploadFiles = itemFiles
      .map((fileItem) => fileItem?.file || fileItem)
      .filter((fileItem) => typeof File !== "undefined" && fileItem instanceof File);
    const existingFileIds = itemFiles
      .map((fileItem) => Number(fileItem?.id || 0))
      .filter(Boolean);
    const examId = Number(item?.[examIdField] || item?.[examField]?.id || 0);
    const resultDate = item?.resultDate || (uploadFiles.length ? moment().format("YYYY-MM-DD") : null);
    const examLabel = (item?.examLabel || "").toString().trim();
    const isResult = Boolean(resultDate || examLabel || existingFileIds.length || uploadFiles.length);

    return {
      ...(item?.id ? { id: item.id } : {}),
      appointmentId: appointment?.id,
      patientId: appointment?.patientId,
      ...(resultDate ? { resultDate } : {}),
      ...(examLabel ? { examLabel } : {}),
      ...(examId > 0 ? { [examIdField]: examId } : {}),
      ...(item?.id && isResult ? { fileIds: existingFileIds } : {}),
      ...(uploadFiles.length ? { files: uploadFiles } : {}),
    };
  }).filter((item) => Boolean(
    item?.id || item?.[examIdField] || item?.resultDate || item?.examLabel || item?.files?.length,
  ));

const getNetworkStatusCode = (error) =>
  Number(
    error?.networkError?.statusCode ||
      error?.networkError?.response?.status ||
      error?.statusCode ||
      0,
  );

const getAppointmentHistoryErrorMessage = (error, payload) => {
  const defaultMessage =
    error?.message || "No se pudo guardar la historia clinica.";
  const message = defaultMessage.toString();
  const normalizedMessage = message.toLowerCase();
  const statusCode = getNetworkStatusCode(error);
  const includesMedicalResultFiles = hasMedicalResultUploadFiles(payload);

  if (normalizedMessage.includes("la cita fue pospuesta")) {
    return "La cita fue pospuesta y no puede completar el proceso médico. Utilice el botón Cerrar para volver a la lista de citas.";
  }

  if (!includesMedicalResultFiles) {
    return defaultMessage;
  }

  if (
    statusCode === 413 ||
    normalizedMessage.includes("status code 413") ||
    normalizedMessage.includes("request entity too large") ||
    normalizedMessage.includes("payload too large")
  ) {
    return "Los archivos de resultados son muy pesados para cargarlos juntos. Borre un archivo o reduzca su tamaño y vuelva a guardar.";
  }

  if (
    [502, 503, 504].includes(statusCode) ||
    normalizedMessage.includes("failed to fetch") ||
    normalizedMessage.includes("network request failed") ||
    normalizedMessage.includes("load failed") ||
    normalizedMessage.includes("unexpected token <") ||
    normalizedMessage.includes("no data found for resource")
  ) {
    return "No pudimos completar la carga de los archivos de resultados. Borre un archivo, reduzca su tamaño o cargue menos archivos por resultado.";
  }

  if (
    normalizedMessage.includes("maxfilesize") ||
    normalizedMessage.includes("max file size") ||
    normalizedMessage.includes("file size") ||
    normalizedMessage.includes("archivo de laboratorio debe ser de")
  ) {
    return "Una de las imagenes de laboratorio supera el tamaño permitido. Borre esa imagen o comprímala antes de guardar.";
  }

  return defaultMessage;
};

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const SEARCH_DEBOUNCE_MS = 350;

// Agrupa las búsquedas mientras se escribe: solo consulta con el último texto y
// todas las llamadas pendientes reciben ese resultado.
const debounceSearch = (search, wait = SEARCH_DEBOUNCE_MS) => {
  let timer;
  let pending = [];
  return (...args) =>
    new Promise((resolve, reject) => {
      pending.push({ resolve, reject });
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const calls = pending;
        pending = [];
        try {
          const result = await search(...args);
          calls.forEach((call) => call.resolve(result));
        } catch (error) {
          calls.forEach((call) => call.reject(error));
        }
      }, wait);
    });
};

// Para buscadores que responden por callback: react-select usaría también la promesa
// devuelta, así que no se retorna nada para no llamar dos veces al callback.
const debounceCallbackSearch = (search, wait) => {
  const debounced = debounceSearch(search, wait);
  return (value, callback) => {
    debounced(value, callback).catch(() => callback([]));
  };
};

// Constantes fuera del hook: así las variables de las consultas no cambian en cada render.
const relationsThePage = {
  relationsGetLastAppoHisto: {
    immovable: [
      "appointmentHistoryMedicalParameter",
      "appointmentHistoryMedicalParameter.medicalParameters",
    ],
    isPageEdit: [
      "appointmentHistoryServices",
      "appointmentHistoryServices.services",
      "appointmentHistoryMedicalPrecription",
      "appointmentHistoryMedicalPrecription.recipeType",
      "appointmentHistoryMedicalPrecription.precriptionDetails",
      "appointmentHistoryMedicalPrecription.precriptionDetails.medicine",
      "appointmentHistoryLaboratory",
      "appointmentHistoryLaboratory.laboratoryExam",
      "appointmentHistoryLaboratory.laboratoryExam.productType",
      "appointmentHistoryLaboratory.files",
      "appointmentHistoryImages",
      "appointmentHistoryImages.imageExam",
      "appointmentHistoryImages.imageExam.productType",
      "appointmentHistoryImages.files",
      "appointmentHistoryLicenses",
      "appointmentHistoryLicenses.licensesTypes",
      "appointmentHistoryReferral",
      "appointmentHistoryReferral.speciality",
      "appointmentHistoryDiagnoses",
      "appointmentHistoryDiagnoses.diagnoses",
      "quotations",
      "quotations.quotationsDetails",
      "quotations.quotationsDetails.product",
    ],
  },
  relationsGetAppointment: [
    "patient",
    "status",
    "services",
    "patient.ars",
    "patient.gender",
    "ars",
  ],
  relationsConsultoryParameters: [
    "consultoryParametersDetail",
    "consultoryParametersDetail.medicalParameters",
  ],
  relationsConsultoryServices: [
    "product",
    "consultoryServicesDetail",
    "consultoryServicesDetail.ars",
  ],
};

export default function useAction(
  medicalStaffId,
  branchOfficeId,
  pharmacyProviderId,
  pageEdit,
  history,
  appointmentIdSelectedInPageclinicHistory,
  isPageModalHistory,
) {
  const [state, setStateBase] = useState(INITIAL_STATE);
  // Último estado para los buscadores, sin recrearlos en cada cambio del formulario.
  const stateRef = useRef(state);
  stateRef.current = state;
  const { params } = useParams();
  const { addToast } = useToasts();
  const client = useApolloClient();
  const isMountedRef = useRef(false);
  const removeServiceWithIdRef = useRef(false);
  const inputRef = useRef(null);
  const pendingNavigationPathRef = useRef(null);
  const {
    hasPrescriptionAvailability,
    refetch: refetchPrescriptionAvailability,
  } = usePrescriptionAvailability({
    medicalStaffId,
    branchOfficeId,
    pharmacyProviderId,
  });
  const setState = useCallback((updater) => {
    if (isMountedRef.current) {
      setStateBase(updater);
    }
  }, [setStateBase]);

  const {
    relationsGetLastAppoHisto: { immovable, isPageEdit },
    relationsGetAppointment,
    relationsConsultoryParameters,
    relationsConsultoryServices,
  } = relationsThePage;

  //<USEMUTATION>
  const [createOrModifyAppointmentHistory] = useMutation(
    CREATE_APPOINTMENT_HISTORY,
  );
  const [updatePatientDateOfBirth] = useMutation(UPDATE_PATIENT_DATE_OF_BIRTH);
  const [updatePatientDeniesHistory] = useMutation(UPDATE_PATIENT_DENIES_HISTORY);
  const [saveAllergyPerPatient] = useMutation(SAVE_ALLERGY_PER_PATIENT);
  //</USEMUTATION>

  const { dataAppointment, activeTabPrescriptions } = state;

  const patientAge = dataAppointment?.patient?.dateOfBirth
    ? moment().diff(moment(dataAppointment.patient.dateOfBirth), "years")
    : null;
  const patientGenderId = dataAppointment?.patient?.gender?.id ?? null;
  const parameterGroupKey = getParameterGroupKey(
    state.tabDataParametersEvaluation?.dataSerched,
  );

  //<USEQUERY>
  const { data: { getAppointment } = {}, loading: loadingAppointment } =
    useQuery(GET_APPOINTMENT_DATA, {
      fetchPolicy: "network-only",
      variables: {
        relations: relationsGetAppointment,
        where: {
          id: appointmentIdSelectedInPageclinicHistory || parseInt(params),
        },
      },
    });

  const {
    data: { getAllergyPerPatient } = {},
    loading: loadingAllergies,
    refetch: refetchAllergies,
  } = useQuery(GET_ALLERGY_PER_PATIENT, {
    // Sin paciente el backend devolvería las alergias de todos los pacientes.
    skip: !dataAppointment?.patientId,
    fetchPolicy: "network-only",
    variables: {
      where: {
        patientId: dataAppointment?.patientId,
        isRemoved: false,
      },
      relations: ["molecule", "allergyGroup"],
    },
  });

  const {
    data: { consultoryParameters } = {},
    loading: loadingConsultoryParameters,
  } = useQuery(CONSULTORY_PARAMETERS, {
    skip: !state?.dataAppointment?.specialityId,
    fetchPolicy: "network-only",
    variables: {
      relations: relationsConsultoryParameters,
      where: {
        medicalStaffId,
        branchOfficeId,
        specialtiesId: state?.dataAppointment?.specialityId,
        patientAge,
        patientGenderId,
      },
    },
  });

  const {
    data: { getLastAppoHisto } = {},
    refetch: refetchGetLastAppoHisto,
    loading: loadingAppointmentHistories,
  } = useQuery(GET_LAST_APPO_HISTO, {
    skip: isEmpty(dataAppointment),
    fetchPolicy: "network-only",
    variables: {
      relations: [...immovable, ...(pageEdit ? isPageEdit : [])],
      where: {
        ...(pageEdit
          ? { appointmentId: dataAppointment?.id }
          : {
              specialityId: dataAppointment?.specialityId,
              patientId: dataAppointment?.patientId,
              medicalStaffId,
            }),
      },
    },
  });

  const {
    data: { consultoryParametersAndOrder } = {},
    loading: loadingConsultoryParametersAndOrder,
  } = useQuery(CONSULTORY_PARAMETERS_AND_ORDER, {
    skip: !state.dataAppointment?.specialityId,
    variables: {
      where: {
        specialtiesId: state.dataAppointment?.specialityId,
        branchOfficeId,
        medicalStaffId,
        patientAge,
        patientGenderId,
      },
    },
  });

  const {
    data: { consultoryServices } = {},
    loading: loadingConsultoryServices,
  } = useQuery(GET_CONSULTORY_SERVICES, {
    skip: state?.activeTab !== 3,
    fetchPolicy: "network-only",
    variables: {
      relations: relationsConsultoryServices,
      where: {
        medicalStaffId,
        branchOfficeId,
        enabled: true,
      },
    },
  });

  const { data: { recipeTypes } = {}, loading: loadingRecipeTypes } = useQuery(
    GET_RECIPE_TYPE,
    {
      skip: state.activeTab !== 4,
      variables: {
        where: { enabled: true },
      },
    },
  );

  const { data: { products } = {}, loading: loadingProducts } = useQuery(
    GET_PRODUCT,
    {
      skip: ![3, 2].includes(activeTabPrescriptions),
      variables: {
        relations: ["productType"],
        where: {
          productTypeId: activeTabPrescriptions === 2 ? 1 : 2,
          enabled: true,
          isAppointment: false,
        },
      },
    },
  );

  const { data: { licensesTypes } = {}, loading: loadingLicensesTypes } =
    useQuery(GET_LICENSES_TYPES, {
      skip: activeTabPrescriptions !== 4,
    });
  //</USEQUERY>
  // debugger;

  //<FUNCTIONS>
  const onChange = useCallback((key, value, key2 = false, key3 = false) => {
    if (!key2)
      return setState((prev) => ({
        ...prev,
        [key]: value,
      }));

    if (!key3)
      return setState((prev) => {
        if (Object.is(prev?.[key]?.[key2], value)) return prev;

        return {
          ...prev,
          edited: true,
          [key]: {
            ...prev[key],
            [key2]: value,
          },
        };
      });

    setState((prev) => {
      if (Object.is(prev?.[key]?.[key2]?.[key3], value)) return prev;

      return {
        ...prev,
        edited: true,
        [key]: {
          ...prev[key],
          [key2]: {
            ...prev[key][key2],
            [key3]: value,
          },
        },
      };
    });
  }, [setState]);

  const handleLoadingModalClosed = useCallback(() => {
    const pendingPath = pendingNavigationPathRef.current;
    if (!pendingPath) return;

    pendingNavigationPathRef.current = null;
    history.push(pendingPath);
  }, [history]);

  const handleExitConsultation = useCallback(() => {
    history.push("/appointment-internal");
  }, [history]);

  const onChangeTabQuotationQuantity = useCallback((productId, value) => {
    setState((prev) => ({
      ...prev,
      edited: true,
      tabQuotes: {
        ...prev.tabQuotes,
        quotations: {
          ...prev.tabQuotes.quotations,
          quotationsDetails: (
            prev?.tabQuotes?.quotations?.quotationsDetails || []
          ).map(
            (item) =>
              item?.product?.id === productId
                ? {
                    ...item,
                    quantity: Number(value) || 0,
                    differencePlusQuantity:
                      (Number(value) || 0) * (item?.consultoryDifferent || 0),
                  }
                : item, // <- SOLO clon al que cambia
          ),
        },
      },
    }));
  }, [setState]);

  const onChangeTabParEvalu = useCallback((key, value, row) => {
    setState((prev) => {
      const {
        tabDataParametersEvaluation: { dataSerched },
      } = prev;
      const data = dataSerched.map((item) =>
        isSameMedicalParameterRow(item, row) ? { ...item, [key]: value } : item,
      );

      return {
        ...prev,
        edited: true,
        tabDataParametersEvaluation: {
          ...prev.tabDataParametersEvaluation,
          dataSerched: data,
        },
      };
    });
  }, [setState]);

  const handleValidations = useCallback(() => {
    let containerErrors = [];

    const {
      tabGenerals,
      tabDataParametersEvaluation: { dataSerched, dataParameterGroupOrder },
      tabServices: { appointmentHistoryServices },
      tabDiagnoses: { appointmentHistoryDiagnoses },
      tabQuotes: { quotations },
      tabPrescription: {
        subTabMedicalLicenses: { appointmentHistoryLicenses },
        subTabReferences: { appointmentHistoryReferral },
      },
    } = state;
    if (!tabGenerals?.reason)
      containerErrors.push(
        "Tab: General => El campo 'Motivo de la Consulta' es Obligatorio. Verifique...",
      );

    if (!tabGenerals?.diseaseHistory)
      containerErrors.push(
        "Tab: General => El campo 'Historia de la Enfermedad:' es Obligatorio. Verifique...",
      );

    const visibleMedicalParameters = getVisibleMedicalParameters(
      dataSerched,
      dataParameterGroupOrder,
    );

    for (const item of visibleMedicalParameters)
      if (
        isMedicalParameterRequired(item, consultoryParameters) &&
        !hasMedicalParameterValue(item)
      )
        containerErrors.push(
          `Tab: Parametros de Evalucacion => En el Grupo de Parametros "${
            dataParameterGroupOrder?.find(
              (itemOrder) =>
                itemOrder?.medicalParameterGroupId ===
                item?.medicalParameterGroupId,
            )?.nameParameterGroup
          }" parametro "${
            item?.medicalParameters?.name
          }" su resultado es obligatorio. Verifique!!!`,
        );

    for (const item of appointmentHistoryServices) {
      if (!item?.quantity)
        containerErrors.push(
          `Tab: Servicio "${item?.services?.name}" su cantidad no puede ser '0'. Verifique!!!`,
        );

      if (item?.services?.requiresResult && !item?.result)
        containerErrors.push(
          `Tab: Servicio "${item?.services?.name}" su resultado es obligatorio. Verifique!!!`,
        );
    }

    if (
      !isEmpty(appointmentHistoryLicenses?.licensesTypes) &&
      (!appointmentHistoryLicenses?.description ||
        !appointmentHistoryLicenses?.days)
    )
      containerErrors.push(
        'Tab "Prescripciones" -> Sub tab "Licencias Medicas" la Justificación y los Dias son Obligatorios. Verifique!!!',
      );

    if (
      !isEmpty(appointmentHistoryReferral?.speciality) &&
      (!appointmentHistoryReferral.medicalName ||
        !appointmentHistoryReferral?.observation)
    )
      containerErrors.push(
        'Tab "Prescripciones" -> Sub tab "Referimientos". Los campos de "Medico" y "Observaciones" son obligatorios. Verifique!!!',
      );

    if (isEmpty(appointmentHistoryDiagnoses))
      containerErrors.push(
        "El campo 'Diagnostico 1' es obligatorio. Verifique!!!",
      );

    if (!isEmpty(quotations?.quotationsDetails))
      for (const item of quotations?.quotationsDetails || [])
        if (!item?.quantity || item?.quantity <= 0)
          containerErrors.push(
            "La cantidad de la cotizacion no puede ser 0. Verifique!!!",
          );

    if (isEmpty(containerErrors)) return false;

    for (const error of containerErrors)
      addToast(error, { appearance: "error", autoDismiss: true });
    return true;
  }, [addToast, consultoryParameters, state]);

  const handleCalculationIMC = useCallback(() => {
    const {
      tabDataParametersEvaluation: { dataSerched, dataParameterGroupOrder },
    } = state;

    let weightLbs = 0; // Peso en libras
    let heightIn = 0; // Altura en pulgadas

    const visibleMedicalParameters = getVisibleMedicalParameters(
      dataSerched,
      dataParameterGroupOrder,
    );

    for (const item of visibleMedicalParameters) {
      if (WEIGHT_PARAMETER_IDS.has(item?.medicalParameters?.id))
        weightLbs = parseFloat(item?.medicalParameterValue) || 0;
      if (HEIGHT_PARAMETER_IDS.has(item?.medicalParameters?.id))
        heightIn = parseFloat(item?.medicalParameterValue) || 0;
    }

    if (!weightLbs || !heightIn) return undefined;

    // Fórmula imperial: IMC = (peso_lbs × 703) / altura_in²
    const imc = (weightLbs * 703) / Math.pow(heightIn, 2);

    return parseFloat(imc.toFixed(2));
  }, [state]);

  const onActionSaveAllergyPerPatient = useCallback(async () => {
    try {
      const { tabAllergies: { data } = {} } = state;

      if (isEmpty(data)) return;

      await saveAllergyPerPatient({
        variables: {
          data,
        },
      });

      refetchAllergies();

      addToast("Alergia guardada correctamente", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast(`Error guardando alergia: ${error.message}`, {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
    }
  }, [addToast, refetchAllergies, saveAllergyPerPatient, state]);

  const handleUpdateDeniesHistory = useCallback(
    async (deniesHistory) => {
      try {
        const patientId = state.dataAppointment?.patientId;
        if (!patientId) return;

        await updatePatientDeniesHistory({
          variables: {
            data: { deniesHistory },
            where: { id: patientId },
          },
        });

        setState((prev) => ({
          ...prev,
          dataAppointment: {
            ...prev.dataAppointment,
            patient: {
              ...prev.dataAppointment?.patient,
              deniesHistory,
            },
          },
        }));

        addToast(
          deniesHistory
            ? "El paciente niega historial de alergias"
            : "Historial de alergias habilitado",
          { appearance: "success", autoDismiss: true },
        );
      } catch (error) {
        addToast(`Error actualizando historial: ${error.message}`, {
          appearance: "error",
          autoDismiss: true,
        });
      }
    },
    [
      addToast,
      setState,
      state.dataAppointment?.patientId,
      updatePatientDeniesHistory,
    ],
  );

  const handleActionMutationAppointmentHistory = useCallback(async () => {
    let payload;

    try {
      if (!state.edited) {
        addToast("No hay cambios que guardar", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      const imc = handleCalculationIMC();

      onChange("isLoadingModal", true);

      const {
        dataAppointment,
        tabGenerals,
        tabDataParametersEvaluation: {
          dataSerched,
          dataParameterGroupOrder,
        },
        tabServices: { appointmentHistoryServices },
        tabDiagnoses: { appointmentHistoryDiagnoses },
        tabPrescription: {
          subTabMedicaments,
          subTabImageStudies: { appointmentHistoryImages },
          subTabExamsLaboratory: { appointmentHistoryLaboratory },
          subTabMedicalLicenses: { appointmentHistoryLicenses },
          subTabReferences: { appointmentHistoryReferral },
        },
        tabQuotes: { quotations: quotationsData },
      } = state;

      if (handleValidations()) return;

      let quotations = undefined;

      if (isEmpty(quotationsData?.quotationsDetails) || quotationsData?.id)
        quotations = quotationsData;

      if (!isEmpty(quotationsData?.quotationsDetails)) {
        const { data: { items } = {} } = await client.query({
          query: OPTIONS,
          variables: {
            where: {
              id: 9,
            },
          },
        });

        const quotationsPayload = quotationsData?.quotationsDetails?.reduce(
          (acc, item) => {
            return {
              ...acc,
              totalInsuredCopay:
                acc.totalInsuredCopay +
                (item.insuredCopay
                  ? item.insuredCopay + acc.totalInsuredCopay
                  : 0),
              totalConsultoryCoverage:
                acc.totalConsultoryCoverage +
                (item.insuredCoverage
                  ? item.insuredCoverage + acc.totalConsultoryCoverage
                  : 0),
              totalConsultoryDifferent:
                acc.totalConsultoryDifferent +
                (item.consultoryDifferent
                  ? item.consultoryDifferent + acc.totalConsultoryDifferent
                  : 0),
              totalConsultoryFee:
                acc.totalConsultoryFee +
                (item?.consultoryFee
                  ? item?.consultoryFee + acc.totalConsultoryFee
                  : 0),
              totalInsuredContrated:
                acc.totalInsuredContrated +
                (item?.insuredContrated
                  ? item?.insuredContrated + acc.totalInsuredContrated
                  : 0),
            };
          },
          {
            totalInsuredCopay: 0,
            totalConsultoryCoverage: 0,
            totalConsultoryDifferent: 0,
            totalConsultoryFee: 0,
            totalInsuredContrated: 0,
          },
        );

        quotations = {
          ...quotationsPayload,
          ...quotationsData,
          statusId: 1,
          patientId: dataAppointment?.patientId,
          pharmacyProviderId,
          appointmentId: dataAppointment?.id,
          arsId: dataAppointment?.arsId,
          branchOfficeId,
          expirationDate: moment()
            .add(items[0]?.value, "days")
            .format("YYYY-MM-DD"),
        };
      }

      const visibleMedicalParameters = getVisibleMedicalParameters(
        dataSerched,
        dataParameterGroupOrder,
      );
      const appointmentHistoryMedicalParameter = visibleMedicalParameters
        .filter(
          (item) => hasMedicalParameterValue(item) || (pageEdit && item?.id),
        )
        .map(({ medicalParameters, id, ...item }) => ({
          ...(pageEdit ? { id } : {}),
          appointmentId: item?.appointmentId,
          patientId: item?.patientId,
          medicalParameterTypeId: item?.medicalParameterTypeId,
          medicalParameterValue: hasMedicalParameterValue(item)
            ? item.medicalParameterValue
            : null,
          medicalParameterGroupId: item?.medicalParameterGroupId,
          observation: item?.observation,
          medicalParameterId: medicalParameters?.id,
          appointmentHistoryId: item?.appointmentHistoryId,
        }));

      const dataPayloadPrescription = {
        ...subTabMedicaments?.appointmentHistoryMedicalPrecription,
        arsId: dataAppointment?.arsId,
        pharmacyProviderId,
        branchOfficeId,
        patientId: dataAppointment?.patientId,
        doctorId: medicalStaffId,
        recipeType: subTabMedicaments?.selectedRecipeTipe,
      };

      const isNewPrescription =
        !dataPayloadPrescription?.id &&
        !isEmpty(subTabMedicaments?.selectedRecipeTipe) &&
        !isEmpty(dataPayloadPrescription?.precriptionDetails);

      if (
        isNewPrescription &&
        !hasPrescriptionAvailability(subTabMedicaments?.selectedRecipeTipe)
      ) {
        addToast(NO_PRESCRIPTION_AVAILABLE_MESSAGE, {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const validationsQuotation =
        isEmpty(quotations) ||
        (!quotations?.id && isEmpty(quotations?.quotationsDetails))
          ? undefined
          : quotations;

      const appointmentHistoryLaboratoryPayload = buildMedicalResultsPayload({
        records: appointmentHistoryLaboratory,
        examIdField: "laboratoryExamId",
        examField: "laboratoryExam",
        appointment: dataAppointment,
      });
      const appointmentHistoryImagesPayload = buildMedicalResultsPayload({
        records: appointmentHistoryImages,
        examIdField: "imageExamId",
        examField: "imageExam",
        appointment: dataAppointment,
      });

      payload = {
        id: tabGenerals?.id,
        date: moment().format("YYYY-MM-DD"),
        reason: tabGenerals?.reason,
        diseaseHistory: tabGenerals?.diseaseHistory,
        imc,
        appointmentId: dataAppointment?.id,
        patientId: dataAppointment?.patientId,
        medicalStaffId: medicalStaffId,
        appointmentHistoryMedicalParameter,
        appointmentHistoryServices,
        appointmentHistoryLaboratory: appointmentHistoryLaboratoryPayload,
        appointmentHistoryImages: appointmentHistoryImagesPayload,
        appointmentHistoryDiagnoses,
        quotations: validationsQuotation,
        appointmentHistoryMedicalPrecription:
          isEmpty(subTabMedicaments?.selectedRecipeTipe) ||
          isEmpty(
            subTabMedicaments?.appointmentHistoryMedicalPrecription
              ?.precriptionDetails,
          )
            ? undefined
            : dataPayloadPrescription,
        appointmentHistoryLicenses: !appointmentHistoryLicenses?.days
          ? undefined
          : appointmentHistoryLicenses,
        appointmentHistoryReferral: isEmpty(
          appointmentHistoryReferral?.speciality,
        )
          ? undefined
          : {
              ...appointmentHistoryReferral,
              patientId: dataAppointment?.patientId,
            },
      };

      await createOrModifyAppointmentHistory({
        variables: {
          data: payload,
        },
      });

      await onActionSaveAllergyPerPatient();

      if (isNewPrescription) refetchPrescriptionAvailability();

      if (pageEdit) {
        await refetchGetLastAppoHisto();
      }

      addToast(
        pageEdit
          ? "Cita médica modificada correctamente"
          : "Cita Medica Guardada de manera exitosa!!",
        {
          appearance: "success",
          autoDismiss: true,
        },
      );
      onChange("edited", false);

      if (!pageEdit) {
        pendingNavigationPathRef.current =
          `/appointment-internal/edit/${dataAppointment.id}`;
      }
    } catch (error) {
      addToast(getAppointmentHistoryErrorMessage(error, payload), {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      onChange("isLoadingModal", false);
    }
  }, [
    addToast,
    branchOfficeId,
    client,
    createOrModifyAppointmentHistory,
    handleCalculationIMC,
    handleValidations,
    hasPrescriptionAvailability,
    medicalStaffId,
    onActionSaveAllergyPerPatient,
    onChange,
    pageEdit,
    pharmacyProviderId,
    refetchGetLastAppoHisto,
    refetchPrescriptionAvailability,
    state,
  ]);

  const onChangeTabServices = useCallback((key, value, serviceId) => {
    setState((prev) => {
      const { tabServices } = prev;
      if (key === "removeHisService")
        return {
          ...prev,
          edited: true,
          tabServices: {
            ...tabServices,
            appointmentHistoryServices:
              tabServices?.appointmentHistoryServices.filter(
                (item) => item.services.id !== serviceId,
              ),
            optionsDataServices: [...tabServices.optionsDataServices, value],
          },
        };

      return {
        ...prev,
        edited: true,
        tabServices: {
          ...tabServices,
          appointmentHistoryServices:
            tabServices?.appointmentHistoryServices.map((item) =>
              item.services.id === serviceId ? { ...item, [key]: value } : item,
            ),
        },
      };
    });
  }, [setState]);

  const handleValidatePatientAllergyGroup = useCallback(
    async (medicineId) => {
      try {
        const { data: { validatePatientAllergyGroup } = {} } =
          await client.query({
            query: VALIDATE_PATIENT_ALLERGY_GROUP,
            fetchPolicy: "network-only",
            variables: {
              where: {
                medicineId,
                patientId: state?.dataAppointment?.patientId,
              },
            },
          });

        if (!isEmpty(validatePatientAllergyGroup))
          throw new Error(
            `El medicamento ${validatePatientAllergyGroup?.medicineName} contiene la molécula 
            ${validatePatientAllergyGroup?.moleculeName}, la cual produce alergia al paciente, 
            según fue configurado por uno de sus médicos`,
          );
        return true;
      } catch (error) {
        addToast(error.message, { appearance: "error", autoDismiss: true });
        return false;
      }
    },
    [addToast, client, state?.dataAppointment?.patientId],
  );

  const handleSelectMedicine = useCallback(
    async (medicine) => {
      if (await handleValidatePatientAllergyGroup(medicine?.id)) {
        onChange(
          "tabPrescription",
          medicine,
          "subTabMedicaments",
          "selectedMedicament",
        );
      }
    },
    [handleValidatePatientAllergyGroup, onChange],
  );

  const searchMedicines = useCallback(
    async (value, callback) => {
      try {
        const {
          tabPrescription: {
            subTabMedicaments: {
              appointmentHistoryMedicalPrecription: { precriptionDetails },
            },
          },
        } = stateRef.current;

        let groupData = [];

        if (value.length < 3) return callback([]);
        const {
          data: { items },
        } = await client.query({
          query: GET_MEDICINE,
          fetchPolicy: "network-only",
          variables: {
            where: {
              // key: 2,
              value: value.toLowerCase(),
              medicalStaffId,
            },
          },
        });
        for (const item of items)
          if (
            isEmpty(precriptionDetails) ||
            !precriptionDetails?.some(
              (itemDetail) => itemDetail?.medicine?.id === item.medicineId,
            )
          )
            groupData.push({ id: item?.medicineId, name: item?.Product_Name });

        callback(groupData);
      } catch (error) {
        throw new Error(error.message);
      }
    },
    [client, medicalStaffId],
  );
  const loadMedicines = useMemo(
    () => debounceCallbackSearch(searchMedicines),
    [searchMedicines],
  );

  const handleValidateFormMedicament = ({
    selectedRecipeTipe,
    selectedMedicament,
    quantity,
    frecuency,
    frecuencyDaysHours,
    duration,
    durationDaysWeeksMonths,
  }) => {
    let errors = [];
    if (isEmpty(selectedRecipeTipe)) errors.push("Tipo de Receta");
    if (isEmpty(selectedMedicament)) errors.push("Medicamentos");
    if (!quantity) errors.push("Cantidad");
    if (!frecuency) errors.push("Frecuencia");
    if (!frecuencyDaysHours) errors.push("Horas/Dias:");
    if (!duration) errors.push("Duración");
    if (!durationDaysWeeksMonths) errors.push("Dias");
    return errors;
  };

  const handleAddMedicament = (resulValueTotal) => {
    try {
      onChange("isLoading", true);
      const {
        tabPrescription: { subTabMedicaments },
      } = state;

      const {
        recipeTypes,
        appointmentHistoryMedicalPrecription: { precriptionDetails },
        selectedRecipeTipe,
        selectedMedicament: medicine,
        ...recipeRest
      } = subTabMedicaments;

      const validations = handleValidateFormMedicament(subTabMedicaments);
      if (!isEmpty(validations))
        return validations.forEach((item) => {
          addToast(`El campo de "${item}" es Obligatorio. Verifique!!!`, {
            appearance: "error",
            autoDismiss: true,
          });
        });

      onChange(
        "tabPrescription",
        {
          ...INITIAL_STATE.tabPrescription.subTabMedicaments,
          recipeTypes,
          selectedRecipeTipe,
          appointmentHistoryMedicalPrecription: {
            ...subTabMedicaments?.appointmentHistoryMedicalPrecription,
            precriptionDetails: [
              ...precriptionDetails,
              {
                totalDosis: resulValueTotal,
                medicine,
                ...recipeRest,
              },
            ],
          },
        },
        "subTabMedicaments",
      );
      inputRef.current.value = "";
    } catch (error) {
      throw new Error(error.message);
    } finally {
      onChange("isLoading", false);
    }
  };

  const loadingSpecialties = useCallback(
    async (value, callback) => {
      try {
        const { data: { specialities } = {} } = await client.query({
          query: GET_SPECIALITIES,
          variables: {
            name: value,
            enabled: true,
          },
        });
        callback(specialities);
      } catch (error) {
        throw new Error(error.message);
      }
    },
    [client],
  );

  const searchMedicalStaffs = useCallback(
    async (value, callback) => {
      try {
        if (value.length < 3) return callback([]);
        const { data: { getRdfMedicalStaffs } = {} } = await client.query({
          query: GET_MEDICAL_STAFFS,
          variables: {
            where: {
              name: value.toLowerCase(),
            },
          },
        });

        onChange(
          "tabPrescription",
          {
            ...stateRef.current.tabPrescription.subTabReferences
              ?.appointmentHistoryReferral,
            medicalName: value,
          },
          "subTabReferences",
          "appointmentHistoryReferral",
        );

        callback(
          uniqBy(getRdfMedicalStaffs, "fullname").map((i) => ({
            value: i.id,
            label: i.fullname,
          })),
        );
      } catch (error) {
        throw new Error(error.message);
      }
    },
    [client, onChange],
  );
  const loadMedicalStaffs = useMemo(
    () => debounceCallbackSearch(searchMedicalStaffs),
    [searchMedicalStaffs],
  );

  const validateAvailableQuantity = useCallback(
    async ({ where }, value) => {
      const { data: { getAvailableQuantity } = {} } = await client.query({
        query: GET_AVAILABLE_QUANTITY,
        variables: {
          where,
        },
      });

      if (
        getAvailableQuantity?.availableQuantity !== undefined &&
        getAvailableQuantity?.availableQuantity < value
      ) {
        addToast(
          `La cantidad maxima de la cotizacion es ${getAvailableQuantity?.availableQuantity}`,
          { appearance: "error", autoDismiss: true },
        );
        onChangeTabServices(
          "quantity",
          parseInt(getAvailableQuantity?.availableQuantity),
          where?.productId,
        );
      }
    },
    [addToast, client, onChangeTabServices],
  );

  const searchDiagnoses = useCallback(
    async (value) => {
      try {
        if (value.length < 3) return [];
        const {
          tabDiagnoses: { appointmentHistoryDiagnoses },
        } = stateRef.current;
        const { data: { diagnoses } = {} } = await client.query({
          query: GET_DIAGNOSES,
          fetchPolicy: "network-only",
          variables: {
            where: {
              enabled: true,
              description: value || null,
              ...(isEmpty(appointmentHistoryDiagnoses)
                ? {}
                : {
                    getIdsDiferrent: appointmentHistoryDiagnoses?.map(
                      (item) => item?.diagnoses?.id,
                    ),
                  }),
            },
          },
        });
        return diagnoses;
      } catch (error) {
        throw new Error(error.message);
      }
    },
    [client],
  );
  const loadDiagnoses = useMemo(
    () => debounceSearch(searchDiagnoses),
    [searchDiagnoses],
  );

  const handleSaveDateOfBirth = useCallback(
    async (dateOfBirth) => {
      try {
        onChange("isLoadingModalDateOfBirth", true);
        const patientId = state?.dataAppointment?.patient?.id;
        if (!patientId) return;

        await updatePatientDateOfBirth({
          variables: {
            data: { dateOfBirth },
            where: { id: patientId },
          },
        });

        setState((prev) => ({
          ...prev,
          isOpenModalDateOfBirth: false,
          isLoadingModalDateOfBirth: false,
          dataAppointment: {
            ...prev.dataAppointment,
            patient: {
              ...prev.dataAppointment.patient,
              dateOfBirth,
            },
          },
        }));

        addToast("Fecha de nacimiento guardada exitosamente.", {
          appearance: "success",
          autoDismiss: true,
        });
      } catch (error) {
        addToast(error.message, { appearance: "error", autoDismiss: true });
        onChange("isLoadingModalDateOfBirth", false);
      }
    },
    [
      addToast,
      onChange,
      setState,
      state?.dataAppointment?.patient?.id,
      updatePatientDateOfBirth,
    ],
  );

  const handleEndConsultation = async ({
    actionsFromModal = null,
    optionSelected = null,
  }) => {
    let didNavigate = false;

    try {
      if (handleValidations()) return;

      onChange("loadingButton2", true);

      if (state.edited && !actionsFromModal && optionSelected === null) {
        return onChange("isOpenModalConfirmEdit", true);
      }

      if (optionSelected === false)
        return onChange("isOpenModalConfirmEdit", false);

      const result = await getDataAppointmentHistoryReport(
        {
          appointmentId: dataAppointment?.id,
        },
        client,
      );
      const companyInfo = await fetchCompanyInfo(client);

      handleShowReportAppointmentHistory({
        ...result,
        ars: dataAppointment?.ars,
        companyInfo,
      });

      didNavigate = true;
      history.push("/appointment-internal");
    } catch (error) {
      throw new Error(error.message);
    } finally {
      if (!didNavigate) {
        onChange("loadingButton2", false);
      }
    }
  };

  const handleLoadAppointmetRefetch = useCallback(() => {
    try {
      if (isEmpty(getLastAppoHisto) || !pageEdit) return;
      onChange("isLoadingModal", true);
      const {
        appointmentHistoryMedicalParameter,
        appointmentHistoryServices,
        appointmentHistoryMedicalPrecription,
        appointmentHistoryImages,
        appointmentHistoryLaboratory,
        appointmentHistoryLicenses,
        appointmentHistoryReferral,
        appointmentHistoryDiagnoses,
        quotations,
        ...rest
      } = getLastAppoHisto;

      setState((prev) => {
        const { tabDataParametersEvaluation, tabPrescription } = prev;
        return {
          ...prev,
          ...(removeServiceWithIdRef.current ? { activeTabQuote: 1 } : {}),
          tabGenerals: rest,
          tabServices: {
            ...prev.tabServices,
            appointmentHistoryServices: appointmentHistoryServices || [],
          },
          tabDataParametersEvaluation: {
            ...tabDataParametersEvaluation,
            dataSerched: appointmentHistoryMedicalParameter ?? [],
          },
          tabDiagnoses: {
            ...prev.tabDiagnoses,
            appointmentHistoryDiagnoses: appointmentHistoryDiagnoses || [],
          },
          tabQuotes: {
            ...prev.tabQuotes,
            quotations: quotations || {},
          },
          tabPrescription: {
            ...tabPrescription,
            subTabMedicaments: {
              ...tabPrescription?.subTabMedicaments,
              selectedRecipeTipe:
                appointmentHistoryMedicalPrecription?.recipeType || {},
              ...(isEmpty(appointmentHistoryMedicalPrecription)
                ? {}
                : { appointmentHistoryMedicalPrecription }),
            },
            subTabExamsLaboratory: {
              ...tabPrescription?.subTabExamsLaboratory,
              appointmentHistoryLaboratory: appointmentHistoryLaboratory || [],
            },
            subTabImageStudies: {
              ...tabPrescription?.subTabImageStudies,
              appointmentHistoryImages: appointmentHistoryImages || [],
            },
            subTabMedicalLicenses: {
              ...tabPrescription?.subTabMedicalLicenses,
              appointmentHistoryLicenses: appointmentHistoryLicenses || [],
            },
            subTabReferences: {
              ...tabPrescription.subTabReferences,
              appointmentHistoryReferral,
            },
          },
        };
      });
    } catch (error) {
      throw new Error(error.message);
    } finally {
      onChange("isLoadingModal", false);
    }
  }, [getLastAppoHisto, onChange, pageEdit, setState]);

  const handleLoadAppointmentData = useCallback(() => {
    try {
      if (isEmpty(getLastAppoHisto) || loadingAppointmentHistories || pageEdit)
        return;
      onChange("isLoadingTabs", true);
      const { appointmentHistoryMedicalParameter } = getLastAppoHisto;
      setState((prev) => {
        const { tabDataParametersEvaluation } = prev;
        const dataMap = tabDataParametersEvaluation?.dataSerched?.map(
          ({ frequencyId, ...item }) => {
            const { id, appointmentHistoryId, appointmentId, ...data } =
              appointmentHistoryMedicalParameter?.find(
                (item2) =>
                  frequencyId === 1 &&
                  item?.medicalParameterGroupId ===
                    item2?.medicalParameterGroupId &&
                  item?.medicalParameters?.id === item2?.medicalParameters?.id,
              ) || {};

            if (isEmpty(data)) return item;
            return {
              id: item?.id,
              appointmentId: item?.appointmentId,
              ...data,
            };
          },
        );

        return {
          ...prev,
          tabDataParametersEvaluation: {
            ...tabDataParametersEvaluation,
            dataSerched: dataMap ?? [],
          },
        };
      });
    } catch (error) {
      throw new Error(error.message);
    } finally {
      onChange("isLoadingTabs", false);
    }
  }, [
    getLastAppoHisto,
    loadingAppointmentHistories,
    onChange,
    pageEdit,
    setState,
  ]);

  //</FUNCTIONS>

  //<USEEFFECTS>

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (consultoryParameters && !loadingConsultoryParameters && !pageEdit) {
      setState((prev) => {
        const { dataAppointment } = prev;
        const appointmentHistoryMedicalParameter = [];
        for (const {
          consultoryParametersDetail,
          medicalParameterGroupId,
        } of consultoryParameters)
          consultoryParametersDetail
            .filter((item) => item.enabled)
            .forEach((item2) => {
              appointmentHistoryMedicalParameter.push({
                id: item2.id,
                appointmentId: dataAppointment?.id,
                patientId: dataAppointment?.patientId,
                medicalParameterGroupId,
                medicalParameters: item2.medicalParameters,
                frequencyId: item2?.frequencyId,
                required: Boolean(item2?.required),
              });
            });
        return {
          ...prev,
          tabDataParametersEvaluation: {
            ...prev.tabDataParametersEvaluation,
            dataSerched: appointmentHistoryMedicalParameter,
          },
        };
      });
    }
  }, [
    consultoryParameters,
    loadingConsultoryParameters,
    pageEdit,
    setState,
  ]);

  useEffect(() => {
    handleLoadAppointmentData();
    handleLoadAppointmetRefetch();
  }, [handleLoadAppointmentData, handleLoadAppointmetRefetch]);

  useEffect(() => {
    if (getAppointment && !loadingAppointment) {
      const appointment = getAppointment[0];
      setState((prev) => ({
        ...prev,
        dataAppointment: appointment,
        isOpenModalDateOfBirth:
          !isPageModalHistory && !appointment?.patient?.dateOfBirth,
      }));
    }
  }, [getAppointment, isPageModalHistory, loadingAppointment, setState]);

  useEffect(() => {
    if (consultoryParametersAndOrder && !loadingConsultoryParametersAndOrder) {
      setState((prev) => {
        const visibleGroups = getVisibleParameterGroups(
          prev.tabDataParametersEvaluation?.dataSerched,
          consultoryParametersAndOrder,
        );
        const currentGroupIds =
          prev.tabDataParametersEvaluation?.dataParameterGroupOrder
            ?.map((item) => item?.medicalParameterGroupId)
            .join("|") || "";
        const nextGroupIds =
          visibleGroups
            ?.map((item) => item?.medicalParameterGroupId)
            .join("|") || "";
        const activeGroupIsVisible = visibleGroups.some(
          (item) =>
            String(item?.medicalParameterGroupId) ===
            String(prev.activeTabParameterEvaluation),
        );
        const nextActiveTab = activeGroupIsVisible
          ? prev.activeTabParameterEvaluation
          : visibleGroups[0]?.medicalParameterGroupId || null;

        if (
          currentGroupIds === nextGroupIds &&
          prev.activeTabParameterEvaluation === nextActiveTab
        ) {
          return prev;
        }

        return {
          ...prev,
          activeTabParameterEvaluation: nextActiveTab,
          tabDataParametersEvaluation: {
            ...prev?.tabDataParametersEvaluation,
            dataParameterGroupOrder: visibleGroups,
          },
        };
      });
    }
  }, [
    consultoryParametersAndOrder,
    loadingConsultoryParametersAndOrder,
    parameterGroupKey,
    setState,
  ]);

  useEffect(() => {
    if (isEmpty(consultoryServices) || loadingConsultoryServices) return;

    setState((prev) => {
      const extractDataServiceId =
        prev?.tabServices?.appointmentHistoryServices?.map(
          (item) => item.services?.id,
        ) || [];

      const optionsDataServices = consultoryServices
        ?.map((item) => {
          const filterDataDetail = item?.consultoryServicesDetail?.find(
            (item2) =>
              parseInt(item2?.ars?.id) === prev?.dataAppointment?.arsId,
          );

          if (extractDataServiceId.includes(item.product?.id)) return null;

          return {
            services: item.product,
            consultoryDifferent: filterDataDetail?.consultoryDifferent,
            insuredCoverage: filterDataDetail?.insuredCoverage,
            consultoryFee: filterDataDetail?.consultoryFee,
            insuredContrated: filterDataDetail?.insuredContrated,
            insuredCopay: filterDataDetail?.insuredCopay,
          };
        })
        .filter((item) => item?.services?.isAppointment === false);

      return {
        ...prev,
        tabServices: {
          ...prev.tabServices,
          optionsDataServices,
        },
      };
    });
  }, [consultoryServices, loadingConsultoryServices, pageEdit, setState]);

  // Las recetas P&P se emiten desde Recetas Digitales, donde se elige el plan
  // y se generan las recetas que este configura; la consulta no las ofrece.
  useEffect(() => {
    if (recipeTypes && !loadingRecipeTypes)
      setState((prev) => ({
        ...prev,
        tabPrescription: {
          ...prev.tabPrescription,
          subTabMedicaments: {
            ...prev.tabPrescription.subTabMedicaments,
            recipeTypes: recipeTypes.filter((recipeType) => !recipeType.isPyp),
          },
        },
      }));
  }, [loadingRecipeTypes, recipeTypes, setState]);

  useEffect(() => {
    if (!products || loadingProducts) return;
    setState((prev) => {
      if (prev?.activeTabPrescriptions === 2)
        return {
          ...prev,
          tabPrescription: {
            ...prev.tabPrescription,
            subTabExamsLaboratory: {
              ...prev.tabPrescription.subTabExamsLaboratory,
              optionsLaboratoryServices: products,
            },
          },
        };

      return {
        ...prev,
        tabPrescription: {
          ...prev.tabPrescription,
          subTabImageStudies: {
            ...prev.tabPrescription.subTabImageStudies,
            optionsImageStudiesServices: products,
          },
        },
      };
    });
  }, [loadingProducts, products, setState]);

  useEffect(() => {
    if (licensesTypes && !loadingLicensesTypes) {
      setState((prev) => ({
        ...prev,
        tabPrescription: {
          ...prev.tabPrescription,
          subTabMedicalLicenses: {
            ...prev.tabPrescription.subTabMedicalLicenses,
            optionsMedicalLicenses: licensesTypes,
          },
        },
      }));
    }
  }, [licensesTypes, loadingLicensesTypes, setState]);

  useEffect(() => {
    if (getAllergyPerPatient && !loadingAllergies) {
      setState((prev) => ({
        ...prev,
        tabAllergies: {
          ...prev.tabAllergies,
          data: getAllergyPerPatient,
        },
      }));
    }
  }, [getAllergyPerPatient, loadingAllergies, setState]);
  //</USEEFFECTS>

  return [
    { state },
    {
      onChange,
      onChangeTabParEvalu,
      loadingConsultoryParametersAndOrder,
      handleActionMutationAppointmentHistory,
      loadingAppointmentHistories,
      loadingConsultoryParameters,
      onChangeTabServices,
      addToast,
      loadMedicines,
      handleAddMedicament,
      loadingSpecialties,
      loadMedicalStaffs,
      loadDiagnoses,
      onChangeTabQuotationQuantity,
      inputRef,
      handleLoadingModalClosed,
      handleExitConsultation,
      handleEndConsultation,
      handleLoadAppointmetRefetch,
      validateAvailableQuantity,
      handleSaveDateOfBirth,
      loadingAllergies,
      handleSelectMedicine,
      handleUpdateDeniesHistory,
    },
  ];
}
